import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { getOrg } from "@/shared/api";

import {
  getFacility,
  getFacilityApprovalReadiness,
  getFacilityAudit,
  listFacilityCourts,
  listFacilityPricingRules,
} from "../api";
import { FacilityActivityPanel } from "../components/FacilityActivityPanel";
import { FacilityAssessmentCenterPanel } from "../components/FacilityAssessmentCenterPanel";
import { FacilityCourtsPanel } from "../components/FacilityCourtsPanel";
import { FacilityPricingPanel } from "../components/FacilityPricingPanel";
import { FacilityQueuePolicyPanel } from "../components/FacilityQueuePolicyPanel";
import {
  FacilityReviewDialogs,
  type FacilityReviewDialog,
} from "../components/FacilityReviewDialogs";
import {
  FacilityReviewPanel,
  useFacilityGovernance,
} from "../components/FacilityReviewPanel";
import { FacilitySnapshotPanel } from "../components/FacilitySnapshotPanel";
import {
  facilityReadState,
  formatFacilityAddress,
} from "../components/facilityDetailFormatters";
import { exactQueryIsAuthoritative } from "../components/facilityGovernanceAuthority";

export function VenueDrilldownPage() {
  const facilityId = useParams<{ id: string }>().id ?? "";
  const queryClient = useQueryClient();
  const facilityKey = ["platform", "facility", facilityId] as const;
  const readinessKey = [
    "platform",
    "facility-approval-readiness",
    facilityId,
  ] as const;
  const [dialogState, setDialogState] = useState<{
    facilityId: string;
    dialog: Exclude<FacilityReviewDialog, null>;
  } | null>(null);
  const { actionError, clearActionError, governancePending, runAction } =
    useFacilityGovernance(facilityId);

  useEffect(() => setDialogState(null), [facilityId]);

  const facilityQuery = useQuery({
    queryKey: facilityKey,
    queryFn: () => getFacility(facilityId),
    enabled: Boolean(facilityId),
    structuralSharing: false,
  });
  const organizationId = facilityQuery.data?.organization_id;
  const organizationQuery = useQuery({
    queryKey: ["platform", "org", organizationId],
    queryFn: () => getOrg(organizationId!),
    enabled: Boolean(organizationId),
  });
  const courtsQuery = useQuery({
    queryKey: ["platform", "facility-courts", facilityId],
    queryFn: () => listFacilityCourts(facilityId),
    enabled: Boolean(facilityId),
  });
  const pricingQuery = useQuery({
    queryKey: ["platform", "facility-pricing", facilityId],
    queryFn: () => listFacilityPricingRules(facilityId),
    enabled: Boolean(facilityId),
  });
  const auditQuery = useQuery({
    queryKey: ["platform", "facility-audit", facilityId],
    queryFn: () => getFacilityAudit(facilityId),
    enabled: Boolean(facilityId),
  });
  const readinessQuery = useQuery({
    queryKey: readinessKey,
    queryFn: () => getFacilityApprovalReadiness(facilityId),
    enabled: Boolean(facilityId),
  });

  const facility = facilityQuery.data;
  const facilityAuthoritative = exactQueryIsAuthoritative(
    queryClient,
    facilityKey,
  );
  const readinessAuthoritative = exactQueryIsAuthoritative(
    queryClient,
    readinessKey,
  );
  const rejectionEligible = Boolean(
    facilityAuthoritative && facility?.approval_status === "in_review",
  );
  const approvalEligible = Boolean(
    rejectionEligible &&
      readinessAuthoritative &&
      readinessQuery.data?.approval_status === "in_review" &&
      readinessQuery.data.can_approve,
  );
  const failed =
    !facility &&
    (facilityQuery.isError || (facilityQuery.isSuccess && !facility));
  const refreshFailed = Boolean(facility && facilityQuery.isError);
  const dialog =
    dialogState?.facilityId === facilityId ? dialogState.dialog : null;

  return (
    <PlatformPage
      title={facility?.name ?? "Facility"}
      description={facility ? formatFacilityAddress(facility) : undefined}
    >
      {facilityQuery.isPending && !facility ? (
        <Box
          role="status"
          aria-label="Loading facility"
          aria-live="polite"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress size={36} />
        </Box>
      ) : failed || !facility ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void facilityQuery.refetch()}
            >
              Retry
            </Button>
          }
        >
          Facility could not be loaded. Try again.
        </Alert>
      ) : (
        <Stack spacing={2.5}>
          {refreshFailed && !dialog && (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void facilityQuery.refetch()}
                >
                  Retry
                </Button>
              }
            >
              Facility data could not be refreshed. Retry to restore current
              governance controls.
            </Alert>
          )}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Link component={RouterLink} to="/facilities" underline="hover">
              Back to facilities
            </Link>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <StatusBadge status={facility.status} />
              <Typography color="text.secondary" variant="body2">
                {facility.timezone}
              </Typography>
            </Stack>
          </Stack>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "minmax(0, 2fr) minmax(280px, 1fr)",
              },
              gap: 2.5,
            }}
          >
            <FacilityReviewPanel
              key={`review:${facilityId}`}
              approvalEligible={approvalEligible}
              facility={facility}
              readiness={{
                data: readinessQuery.data,
                state: facilityReadState(readinessQuery),
                onRetry: () => void readinessQuery.refetch(),
              }}
              readinessAuthoritative={readinessAuthoritative}
              readinessRetrying={readinessQuery.fetchStatus !== "idle"}
              governancePending={governancePending}
              rejectionEligible={rejectionEligible}
              onOpenDialog={(next) =>
                setDialogState({ facilityId, dialog: next })
              }
              onRetryReadiness={() => void readinessQuery.refetch()}
            />
            <FacilityAssessmentCenterPanel
              key={`assessment:${facilityId}`}
              facility={facility}
              facilityAuthoritative={facilityAuthoritative}
              facilityRetrying={facilityQuery.fetchStatus !== "idle"}
              governancePending={governancePending}
              error={actionError}
              onAction={runAction}
              onRetryFacility={() => void facilityQuery.refetch()}
            />
            <FacilitySnapshotPanel
              facility={facility}
              organization={{
                data: organizationQuery.data,
                state: facilityReadState(organizationQuery),
                onRetry: () => void organizationQuery.refetch(),
              }}
            />
            <FacilityQueuePolicyPanel facility={facility} />
            <FacilityCourtsPanel
              courts={{
                data: courtsQuery.data,
                state: facilityReadState(courtsQuery),
                onRetry: () => void courtsQuery.refetch(),
              }}
            />
            <FacilityPricingPanel
              pricing={{
                data: pricingQuery.data,
                state: facilityReadState(pricingQuery),
                onRetry: () => void pricingQuery.refetch(),
              }}
            />
          </Box>
          <FacilityActivityPanel
            activity={{
              data: auditQuery.data,
              state: facilityReadState(auditQuery),
              onRetry: () => void auditQuery.refetch(),
            }}
          />
          <FacilityReviewDialogs
            key={`dialogs:${facilityId}`}
            facilityId={facilityId}
            facilityName={facility.name}
            dialog={dialog}
            approvalEligible={approvalEligible}
            facilityAuthoritative={facilityAuthoritative}
            facilityRetrying={facilityQuery.fetchStatus !== "idle"}
            pending={governancePending}
            error={actionError}
            readinessAuthoritative={readinessAuthoritative}
            readinessRetrying={readinessQuery.fetchStatus !== "idle"}
            rejectionEligible={rejectionEligible}
            onAction={runAction}
            onRetryApproval={() => {
              if (!facilityAuthoritative) void facilityQuery.refetch();
              if (!readinessAuthoritative) void readinessQuery.refetch();
            }}
            onRetryFacility={() => void facilityQuery.refetch()}
            onClose={() => {
              if (dialog) clearActionError(dialog);
              setDialogState(null);
            }}
          />
        </Stack>
      )}
    </PlatformPage>
  );
}
